import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { banHangListParamsFromQueryString } from '@/utils/banHangListQueryUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'

const PREFIX = '/tra-hang'

const blobResponseConfig = { responseType: 'blob' as const }

const parseBlobErrorIfJson = async (blob: Blob): Promise<never> => {
  const text = await blob.text()
  try {
    const j = JSON.parse(text) as { message?: string }
    throw new Error(j?.message || 'Yeu cau that bai')
  } catch (e) {
    if (e instanceof SyntaxError) throw new Error(text.trim().slice(0, 240) || 'Yeu cau that bai')
    throw e
  }
}

const ensureExcelBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type && blob.type.includes('application/json')) await parseBlobErrorIfJson(blob)
  return blob
}

export const fetchPhieuTraHangList = (search: string): Promise<PagedListResult<Record<string, unknown>>> => {
  const params = banHangListParamsFromQueryString(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-phieu-tra-hang`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return { data: data as Record<string, unknown>[], paging, summary }
    }),
  )
}

export const xuatPhieuTraHangTongQuanBlob = async (search?: string): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-tong-quan-tra-hang`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const xuatPhieuTraHangChiTietBlob = async (search?: string): Promise<Blob> => {
  const params = search ? banHangListParamsFromQueryString(search) : {}
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${PREFIX}/xuat-file-chi-tiet-tra-hang`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export const timHoaDonChoTraHang = (search: string) => {
  const params = banHangListParamsFromQueryString(search)
  return apiRequest(ApisServiceApp.get(`${PREFIX}/tim-hoa-don`, { params }))
}

export const taoPhieuTraHang = (body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/tao-phieu-tra-hang`, body))

export const taoPhieuTraHangTheoHoaDon = (hoaDonId: string | number, body?: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${hoaDonId}/tao-phieu-tra-hang-theo-hoa-don`, body ?? {}))

export const fetchPhieuTraHangDetail = (id: string | number) =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-phieu-tra-hang`).then((res: unknown) => ({
      data: normalizeDetailResponse(res as { data?: Record<string, unknown> }),
    })),
  )

export const capNhatPhieuTraHang = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-phieu-tra-hang`, body))

/** Sửa tại chỗ ghi chú / kênh bán / người bán — không hủy/tạo lại phiếu nhập trả. */
export const capNhatThongTinPhieuTraHang = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/cap-nhat-thong-tin-phieu-tra-hang`, body))

export const xoaPhieuTraHang = (id: string | number) =>
  apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-phieu-tra-hang`))

export const huyPhieuTraHang = (id: string | number) =>
  apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/huy-phieu-tra-hang`, {}))

export const thanhToanPhieuTraHang = (id: string | number, body: Record<string, unknown>) =>
  apiRequest(ApisServiceApp.post(`${PREFIX}/${id}/thanh-toan-phieu-tra-hang`, body))
